import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ShieldCheck, Zap, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared layout + submit handler for every /partner/:type intake form.
 *
 * Props:
 *   partnerType   — "printer" | "material" | "logistics"
 *   title / lede  — hero copy
 *   accentHue     — matches the hub tile
 *   benefits      — array of { icon, title, body }
 *   extraFields   — JSX for the type-specific fields, rendered inside the form
 *   extraState    — object held in parent state and sent as `extra`
 *   onExtraChange — setter for extraState
 *   validateExtra — optional () => string | null (error message)
 */
export function PartnerFormShell({
  partnerType, title, lede, accentHue, benefits,
  extraFields, extraState, validateExtra, testIdPrefix,
}) {
  const [form, setForm] = useState({
    legal_name: "", contact_person: "", email: "", phone: "",
    city: "", state: "", pincode: "", gstin: "", website: "",
    about: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) {
      setErr("Please tick the consent box so we can process your application.");
      return;
    }
    if (validateExtra) {
      const msg = validateExtra();
      if (msg) { setErr(msg); return; }
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/partner-applications`, {
        ...form,
        partner_type: partnerType,
        extra: extraState || {},
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      setErr(typeof detail === "string"
        ? detail
        : "Could not submit. Please try again or email info@goprintpack.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="m-shell" data-testid={`${testIdPrefix}-page`}>
      <header className="m-nav">
        <Link to="/" className="m-brand">
          <img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/>
        </Link>
        <nav className="m-nav-links">
          <Link to="/">Home</Link>
          <Link to="/partner">All partner types</Link>
          <Link to="/contact">Contact</Link>
        </nav>
        <div className="m-nav-actions">
          <Link to="/login" className="m-ghost">Sign in</Link>
        </div>
      </header>

      {sent ? (
        <section className="pf-sent" data-testid={`${testIdPrefix}-sent`}>
          <div className="pf-sent-badge" style={{background: `${accentHue}22`, color: accentHue}}>
            <CheckCircle2 size={30} strokeWidth={2}/>
          </div>
          <h1 className="pf-h1">Application received.</h1>
          <p className="pf-sent-lead">
            Thanks for applying to join GoPrintPack as a <b>{title}</b>. We'll review your
            details and reach out within <b>2 business days</b> via phone and email to walk
            through verification and bank onboarding.
          </p>
          <div className="pf-sent-ctas">
            <Link to="/" className="m-cta m-cta-lg" data-testid={`${testIdPrefix}-sent-home`}>
              Back home <ArrowRight size={14}/>
            </Link>
            <Link to="/partner" className="m-outline" data-testid={`${testIdPrefix}-sent-another`}>
              Apply for another role
            </Link>
          </div>
          <p className="pf-sent-note">
            In the meantime, write to <a href="mailto:info@goprintpack.com">info@goprintpack.com</a> if
            you want to attach a company profile, catalogue or certifications.
          </p>
        </section>
      ) : (
        <section className="pf-shell">
          <aside className="pf-side" style={{borderLeftColor:accentHue}}>
            <p className="eyebrow" style={{color:accentHue}}>APPLY · {partnerType.toUpperCase()}</p>
            <h1 className="pf-h1">{title}</h1>
            <p className="pf-lede">{lede}</p>
            <div className="pf-benefits">
              {benefits.map((b, i) => (
                <div className="pf-benefit" key={i}>
                  <div className="pf-benefit-icon" style={{background:`${accentHue}22`,color:accentHue}}>
                    {b.icon}
                  </div>
                  <div>
                    <h4>{b.title}</h4>
                    <p>{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="pf-trust">
              <div><ShieldCheck size={14}/> Escrow before every payout</div>
              <div><Zap size={14}/> 48-hour onboarding</div>
            </div>
          </aside>

          <form className="pf-form" onSubmit={submit} data-testid={`${testIdPrefix}-form`}>
            {err && <div className="pf-err" data-testid={`${testIdPrefix}-error`}>{err}</div>}

            <fieldset>
              <legend>Business details</legend>
              <Row>
                <Field label="Business / legal name *" name="legal_name" value={form.legal_name}
                       onChange={set} required maxLength={160} testid={`${testIdPrefix}-legal-name`}/>
                <Field label="Contact person *" name="contact_person" value={form.contact_person}
                       onChange={set} required maxLength={120} testid={`${testIdPrefix}-contact-person`}/>
              </Row>
              <Row>
                <Field label="Email *" type="email" name="email" value={form.email}
                       onChange={set} required maxLength={120} testid={`${testIdPrefix}-email`}/>
                <Field label="Phone / WhatsApp *" name="phone" value={form.phone}
                       onChange={set} required maxLength={30} testid={`${testIdPrefix}-phone`}/>
              </Row>
              <Row>
                <Field label="City" name="city" value={form.city} onChange={set} maxLength={80} testid={`${testIdPrefix}-city`}/>
                <Field label="State" name="state" value={form.state} onChange={set} maxLength={80} testid={`${testIdPrefix}-state`}/>
                <Field label="Pincode" name="pincode" value={form.pincode} onChange={set} maxLength={10} testid={`${testIdPrefix}-pincode`}/>
              </Row>
              <Row>
                <Field label="GSTIN" name="gstin" value={form.gstin} onChange={set}
                       placeholder="27ABCDE1234F1Z5" maxLength={20} testid={`${testIdPrefix}-gstin`}/>
                <Field label="Website / Instagram" name="website" value={form.website}
                       onChange={set} placeholder="https://..." maxLength={400} testid={`${testIdPrefix}-website`}/>
              </Row>
            </fieldset>

            {extraFields && (
              <fieldset>
                <legend>Capabilities</legend>
                {extraFields}
              </fieldset>
            )}

            <fieldset>
              <legend>Tell us about your business *</legend>
              <textarea
                name="about" rows={5} value={form.about} onChange={set}
                minLength={20} maxLength={1500} required
                placeholder="Years in business, team size, specialties, flagship clients or categories you serve. A short pitch — we read every word."
                data-testid={`${testIdPrefix}-about`}
                className="pf-textarea"
              />
            </fieldset>

            <label className="pf-consent" data-testid={`${testIdPrefix}-consent-wrap`}>
              <input
                type="checkbox" name="consent" checked={form.consent} onChange={set}
                data-testid={`${testIdPrefix}-consent`}
              />
              <span>
                I confirm the information is accurate and I agree to the{" "}
                <Link to="/terms" target="_blank">Terms</Link>,{" "}
                <Link to="/privacy" target="_blank">Privacy Policy</Link> and{" "}
                <Link to="/acceptable-use" target="_blank">Acceptable Use Policy</Link>.
              </span>
            </label>

            <button
              type="submit" disabled={busy}
              className="m-cta m-cta-lg pf-submit"
              style={{background:accentHue}}
              data-testid={`${testIdPrefix}-submit`}
            >
              {busy ? "Submitting..." : <>Submit application <ArrowRight size={14}/></>}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}

/* ----- Primitives ----- */
function Row({ children }) { return <div className="pf-row">{children}</div>; }

function Field({ label, type = "text", testid, ...rest }) {
  return (
    <label className="pf-field">
      <span>{label}</span>
      <input type={type} data-testid={testid} {...rest}/>
    </label>
  );
}

export function Select({ label, testid, children, ...rest }) {
  return (
    <label className="pf-field">
      <span>{label}</span>
      <select data-testid={testid} {...rest}>{children}</select>
    </label>
  );
}

export function Pill({ label, active, onClick, testid }) {
  return (
    <button
      type="button" onClick={onClick}
      data-testid={testid}
      className={`pf-pill ${active ? "pf-pill-on" : ""}`}
    >{label}</button>
  );
}

export { Row, Field };
